import { getBrandDetailsFromBrandJson } from './share';
import { isTestEnvironment } from '../apiCaching/POST-Requests/helperModules/helperFunctions';

/**
 * Brand Loading Manager - Ensures brands.json is loaded before footer operations
 * Prevents footer config 404 errors by controlling the loading sequence
 */
class BrandLoadingManager {
    private static instance: BrandLoadingManager;
    private brandLoadingPromise: Promise<any> | null = null;
    private isBrandLoaded = false;
    private readonly maxWaitTime = 10000; // 10 seconds max wait

    private constructor() {}

    public static getInstance(): BrandLoadingManager {
        if (!BrandLoadingManager.instance) {
            BrandLoadingManager.instance = new BrandLoadingManager();
        }
        return BrandLoadingManager.instance;
    }

    private isBrandDataAvailable(): boolean {
        const brandData = localStorage.getItem('brandDetails');
        return brandData !== null && 
               brandData !== 'undefined' && 
               brandData.length > 0;
    }

    /**
     * Load brand data and cache the promise to avoid multiple calls
     */
    private async loadBrandData(): Promise<any> {
        if (this.brandLoadingPromise) {
            return this.brandLoadingPromise;
        }

        const conceptCodeElement = document.getElementById('conceptCode') as HTMLInputElement;
        const conceptCode = conceptCodeElement?.value;

        if (!conceptCode) {
            if (isTestEnvironment()) {
                console.warn('ConceptCode not found - skipping brand data loading');
            }
            return null;
        }

        this.brandLoadingPromise = getBrandDetailsFromBrandJson(conceptCode)
            .then((brandData) => {
                this.isBrandLoaded = true;
                return brandData;
            })
            .catch((error) => {
                if (isTestEnvironment()) {
                    console.error('Failed to load brand data:', error);
                }
                this.isBrandLoaded = false;
                return null;
            });

        return this.brandLoadingPromise;
    }

    public async waitForBrandData(): Promise<boolean> {
        if (this.isBrandDataAvailable()) {
            this.isBrandLoaded = true;
            return true;
        }

        // If already loading, wait for the existing promise
        if (this.brandLoadingPromise) {
            try {
                await this.brandLoadingPromise;
                return this.isBrandLoaded;
            } catch (error) {
                if (isTestEnvironment()) {
                    console.error('Error waiting for existing brand loading promise:', error);
                }
                return false;
            }
        }

        try {
            const timeoutPromise = new Promise<null>((_, reject) => {
                setTimeout(() => {
                    reject(new Error(`Brand data loading timeout after ${this.maxWaitTime}ms`));
                }, this.maxWaitTime);
            });

            await Promise.race([
                this.loadBrandData(),
                timeoutPromise
            ]);

            return this.isBrandLoaded;
        } catch (error) {
            if (isTestEnvironment()) {
                console.warn('Brand data loading failed or timed out:', error);
            }
            return false;
        }
    }
}

export function getBrandLoadingManager(): BrandLoadingManager {
    return BrandLoadingManager.getInstance();
}

export async function waitForBrandDataBeforeFooterConfig(): Promise<boolean> {
    const manager = getBrandLoadingManager();
    return await manager.waitForBrandData();
}
